import React, { useState } from 'react';
import { Stack } from '@mui/system';
import { useQuery, useMutation } from '@apollo/client/react';

import { BasePage } from '../../components/base-page';
import { UnitCard } from '../../components/unit-card';
import {
  GetUnitsDocument,
  CreateUnitDocument,
  GetUnitsQuery,
  GetUnitsQueryVariables,
  CreateUnitMutation,
  CreateUnitMutationVariables,
} from '../../generated/graphql';
import { useNavigate, useSearchParams } from 'react-router';
import { appPaths } from '../../app-paths';
import { NewUnitDialog } from '../../components/new-unit-dialog';
import { NewCard } from '../../components/new-card';

import { useAlert } from '@dwelvio/react-alert';
import { Button, Typography } from '@mui/material';

const FilterGroupsArray = ['active', 'archived'] as const;
type FilterGroupType = (typeof FilterGroupsArray)[number];

export function UnitListPage() {
  const navigate = useNavigate();
  const { show } = useAlert();
  const [showAddNew, setShowAddNew] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const filterGroup = (searchParams.get('fg') as FilterGroupType) ?? 'active';
  if (filterGroup && FilterGroupsArray.includes(filterGroup as FilterGroupType) === false) {
    searchParams.set('fg', 'active');
    setSearchParams(searchParams);
  }

  const {
    data: unitData,
    refetch: refetchUnits,
    loading: unitsLoading,
    error: unitsError,
  } = useQuery<GetUnitsQuery, GetUnitsQueryVariables>(GetUnitsDocument, {
    variables: { includeArchived: true },
  });

  React.useEffect(() => {
    if (unitsError) {
      show.error('Nepodarilo sa načítať nehnuteľnosti.', unitsError.message);
    }
  }, [unitsError, show]);

  const [createUnit, { loading: createLoading }] = useMutation<CreateUnitMutation, CreateUnitMutationVariables>(
    CreateUnitDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa vytvoriť nehnuteľnosť.', e.message);
      },
    },
  );

  const handleAddNew = (name: string) => {
    createUnit({
      variables: { input: { name } },
      onCompleted: () => {
        setShowAddNew(false);
        refetchUnits();
      },
    });
  };

  const units = React.useMemo(
    () =>
      (unitData?.getUnits ?? []).filter((p) =>
        filterGroup === 'archived' ? p.status === 'archived' : p.status !== 'archived',
      ),
    [unitData, filterGroup],
  );

  // Sort units ascending by name
  const sortedUnits = React.useMemo(() => [...units].sort((a, b) => a.name.localeCompare(b.name)), [units]);

  return (
    <BasePage
      title="Nehnuteľnosti"
      loading={unitsLoading}
      errorMsg={unitsError ? 'Nepodarilo na načítať nehnuteľnosti.' : undefined}
    >
      <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }}>
        <Button
          variant={filterGroup != 'active' ? 'outlined' : 'contained'}
          onClick={() => {
            searchParams.set('fg', 'active');
            setSearchParams(searchParams);
          }}
        >
          Aktívne
        </Button>
        <Button
          variant={filterGroup != 'archived' ? 'outlined' : 'contained'}
          onClick={() => {
            searchParams.set('fg', 'archived');
            setSearchParams(searchParams);
          }}
        >
          Archivované
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap flexWrap="wrap">
        {sortedUnits.length === 0 && (
          <Typography variant="body1" sx={{ width: '100%' }}>
            {filterGroup === 'archived'
              ? 'Zatiaľ nemáte žiadne archivované nehnuteľnosti.'
              : 'Zatiaľ nemáte žiadne evidované nehnuteľnosti.'}
          </Typography>
        )}
        {sortedUnits
          .filter((p) => !p.deletedAt)
          .map((p) => (
            <UnitCard key={p.id} unit={p} onClick={() => navigate(appPaths.unit.navigate({ unitId: p.id }))} />
          ))}
        {filterGroup === 'active' && (
          <NewCard label="Pridať" width={300} height={285} onClick={() => setShowAddNew(true)} />
        )}
      </Stack>

      {showAddNew && (
        <NewUnitDialog onClose={() => setShowAddNew(false)} onSubmit={handleAddNew} loading={createLoading} />
      )}
    </BasePage>
  );
}
